import { CopilotNotificationSurfacer } from './copilot-notification-surfacer';

export const NOTIFICATIONS_FOLLOW_UP = 'You noticed something on this page. Tell me about it.';

export type CopilotTurnKind = 'user' | 'continuation' | 'notifications';

/** What the user sent while Tailor could not take it yet — all of it, images included. */
export interface QueuedUserTurn {
  text: string;
  images?: string[];
}

/** A turn the scheduler has just started: the panel sends it. */
export interface StartedTurn {
  id: number;
  kind: CopilotTurnKind;
  message: string;
  /** Hidden turns are Tailor's own; the user's turns are shown. */
  hidden: boolean;
  images?: string[];
}

interface Turn {
  id: number;
  kind: CopilotTurnKind;
  /**
   * Streaming: a request is running and its cleanup will end the turn.
   * Awaiting approval: the request finished on an approval card; the resumed
   * reply continues the turn, and until then no request is running.
   */
  phase: 'streaming' | 'awaitingApproval';
  /** The user pressed Stop, or started a new chat, during it: nothing that arrives for it later counts. */
  ended: boolean;
  /** What the page refused during it, to be continued when it ends. */
  refusals: string[];
}

/**
 * Who has Tailor's one turn slot, and who gets it next.
 *
 * Tailor runs one turn at a time. The slot is free or owned by a numbered
 * turn, and passes straight from one turn to the next when a turn ends — there
 * is no moment where Tailor looks free while something is waiting. When the
 * slot frees, it goes to, in order:
 * 1. the continuation of the turn that just ended, when the page refused
 *    something Tailor did in it — part of that turn, so it goes first, once:
 *    a refusal during the continuation goes to the user instead;
 * 2. the message the user sent meanwhile — their next turn;
 * 3. Tailor bringing up notifications the user has not been shown, while the
 *    panel is open and the user has not just pressed Stop.
 *
 * Page actions are tagged with the turn that sent them, so a refusal can only
 * continue its own turn, and only while that turn is still running. Stop ends
 * the current turn's claim on anything: what arrives for it later goes to the
 * user, and Tailor starts nothing of its own until the user speaks or reopens
 * the panel. A message the user already queued still goes next.
 */
export class CopilotTurnScheduler {
  private nextId = 1;
  private chat = 1;
  private current: Turn | null = null;
  private queuedUser: QueuedUserTurn | null = null;
  /** After Stop: no turns of Tailor's own until the user speaks or reopens the panel. */
  private quiet = false;
  private readonly surfacer = new CopilotNotificationSurfacer();

  /** The conversation on screen. A new chat is a new number: work begun in the old one no longer belongs. */
  get chatId(): number {
    return this.chat;
  }

  isCurrentChat(chatId: number): boolean {
    return chatId === this.chat;
  }

  get busy(): boolean {
    return this.current !== null;
  }

  get currentTurnId(): number | null {
    return this.current?.id ?? null;
  }

  get queuedUserText(): string | undefined {
    return this.queuedUser?.text;
  }

  /** The user sent a message: started now when Tailor is free, otherwise held (whole) as their next turn. */
  userSends(turn: QueuedUserTurn): StartedTurn | null {
    if (this.current) {
      this.queuedUser = turn;

      return null;
    }

    return this.startUser(turn);
  }

  /** The current turn's request settled: the slot passes to the next turn, if any. */
  turnEnded(state: { open: boolean; unseenIds: readonly string[] }): StartedTurn | null {
    const ended = this.current;

    this.current = null;

    if (ended && !ended.ended && ended.refusals.length) {
      return this.start('continuation', continuationMessage(ended.refusals));
    }

    if (this.queuedUser) {
      const queued = this.queuedUser;

      this.queuedUser = null;

      return this.startUser(queued);
    }

    return this.idle(state);
  }

  /** Tailor is free: whether it should bring up notifications now. */
  idle(state: { open: boolean; unseenIds: readonly string[] }): StartedTurn | null {
    if (this.current || this.quiet) {
      return null;
    }

    return this.surfacer.shouldSurface({ open: state.open, busy: false, unseenIds: state.unseenIds })
      ? this.start('notifications', NOTIFICATIONS_FOLLOW_UP)
      : null;
  }

  /**
   * The page refused an action Tailor took in turn `turnId`, saying why.
   * Continued when that turn ends if it is still running, was not stopped, and
   * is not itself a continuation; otherwise the user should be told.
   */
  refused(turnId: number | null, reason: string): 'continue' | 'tell-user' {
    const turn = this.current;

    if (!turn || turn.id !== turnId || turn.ended || turn.kind === 'continuation') {
      return 'tell-user';
    }

    turn.refusals.push(reason);

    return 'continue';
  }

  /** The user pressed Stop. */
  stop(): void {
    this.endCurrentClaim();
    this.quiet = true;
  }

  /** A turn started outside the scheduler (a retry of the user's last message, an answered approval card): it owns the slot. */
  adopt(): void {
    // A stopped turn whose request has not settled yet gives way: the request
    // starting now owns the slot, not one the user already ended.
    if (!this.current || this.current.ended) {
      this.current = { id: this.nextId++, kind: 'user', phase: 'streaming', ended: false, refusals: [] };
    }
  }

  /**
   * The current turn's request finished on an approval card: the turn keeps
   * the slot until it resumes. A turn that was stopped, or belongs to a chat
   * since replaced, has no card left to answer — false, and it ends as usual.
   */
  awaitingApproval(): boolean {
    if (!this.current || this.current.ended) {
      return false;
    }

    this.current.phase = 'awaitingApproval';

    return true;
  }

  /** The approval was answered: the turn's reply resumes. */
  resumed(): void {
    if (this.current) {
      this.current.phase = 'streaming';
    }
  }

  clearQueuedUser(): void {
    this.queuedUser = null;
  }

  panelOpenChanged(open: boolean): void {
    if (open) {
      this.quiet = false;
    } else {
      // Closing ends this opening: what was brought up may be brought up again on the next.
      this.surfacer.shouldSurface({ open: false, busy: false, unseenIds: [] });
    }
  }

  /**
   * A new chat: nothing from the old one carries over. A reply still streaming
   * keeps the slot until its cleanup ends it; a turn paused on an approval
   * card has no request left to do that, so it lets go now.
   */
  reset(): void {
    this.chat += 1;
    // Stop's quiet belonged to the old chat.
    this.quiet = false;
    // So did what it brought up: a reply cut short never showed it.
    this.surfacer.shouldSurface({ open: false, busy: false, unseenIds: [] });

    if (this.current?.phase === 'awaitingApproval') {
      this.current = null;
    } else {
      this.endCurrentClaim();
    }

    this.queuedUser = null;
  }

  private endCurrentClaim() {
    if (this.current) {
      this.current.ended = true;
      this.current.refusals = [];
    }
  }

  private startUser(turn: QueuedUserTurn): StartedTurn {
    // The user speaking ends any quiet after Stop.
    this.quiet = false;

    return { ...this.start('user', turn.text), images: turn.images };
  }

  private start(kind: CopilotTurnKind, message: string): StartedTurn {
    this.current = { id: this.nextId++, kind, phase: 'streaming', ended: false, refusals: [] };

    return { id: this.current.id, kind, message, hidden: kind !== 'user' };
  }
}

function continuationMessage(reasons: readonly string[]): string {
  return [
    'Something you did on the page did not take effect:',
    ...reasons.map((reason) => `- ${reason}`),
    "The page as it is now is in your context. Tell me briefly, and redo it if it's still wanted.",
  ].join('\n');
}
